import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Wallet } from 'lucide-react';

const formatCR = (n) => Math.round(parseFloat(n) || 0).toLocaleString('fr-FR');

export default function EvolutionTresorerie({ user, onLogout }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/financial/tresorerie?year=${year}`);
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [API, year]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const { solde_initial, flux_mensuel } = data;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="evolution-tresorerie-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2" data-testid="evolution-back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Wallet className="h-7 w-7 text-blue-600" /> Évolution mensuelle cumulée
            </h1>
            <p className="text-gray-600 text-sm">Solde de trésorerie mois par mois — {year}</p>
          </div>
          <div>
            <Label className="text-xs">Année</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="shadow-lg border-2 border-blue-100">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" /> Évolution mensuelle cumulée — {year}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-50 text-blue-900 border-b">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Mois</th>
                  <th className="px-4 py-2 text-right font-semibold">Encaissements</th>
                  <th className="px-4 py-2 text-right font-semibold">Décaissements</th>
                  <th className="px-4 py-2 text-right font-semibold">Solde net</th>
                  <th className="px-4 py-2 text-right font-bold bg-blue-100">Solde cumulé</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-50">
                <tr className="border-b bg-gray-50/50 italic">
                  <td className="px-4 py-2 text-gray-600">Solde initial ({year})</td>
                  <td colSpan={3}></td>
                  <td className="px-4 py-2 text-right font-bold text-gray-700 bg-gray-100">{formatCR(solde_initial)}</td>
                </tr>
                {flux_mensuel.map((f, i) => (
                  <tr key={i} className="hover:bg-blue-50/40">
                    <td className="px-4 py-2 font-medium">{f.mois}</td>
                    <td className="px-4 py-2 text-right text-green-700">{f.encaissements ? `+${formatCR(f.encaissements)}` : '—'}</td>
                    <td className="px-4 py-2 text-right text-red-700">{f.decaissements ? `−${formatCR(f.decaissements)}` : '—'}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${f.solde_net >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                      {f.solde_net !== 0 ? (f.solde_net > 0 ? `+${formatCR(f.solde_net)}` : `${formatCR(f.solde_net)}`) : '—'}
                    </td>
                    <td className={`px-4 py-2 text-right font-bold ${f.solde_cumul >= 0 ? 'bg-blue-50 text-blue-800' : 'bg-red-50 text-red-800'}`}>
                      {formatCR(f.solde_cumul)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
